import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { isArray, isEmpty } from 'radash';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';

import { ReactComponent as PackagingUnitBase } from '@/assets/icons/packaging-unit-base.svg';
import { ReactComponent as PackagingUnitCase } from '@/assets/icons/packaging-unit-case.svg';
import { ReactComponent as PackagingUnitContainer } from '@/assets/icons/packaging-unit-container.svg';
import { ReactComponent as PackagingUnitDisplay } from '@/assets/icons/packaging-unit-display.svg';
import { ReactComponent as PackagingUnitPallet } from '@/assets/icons/packaging-unit-pallet.svg';
import { ButtonDropdown } from '@/components/core/ButtonDropdown/ButtonDropdown';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/core/DropdownMenu/DropdownMenu';
import { Input } from '@/components/core/Input';
import LabelPanel from '@/components/core/LabelPanel/LabelPanel';
import { Loader } from '@/components/core/Loader';
import { DiscardChangesConfirmationModal } from '@/components/DiscardChangesConfirmationModal';
import { NodePackagingLevel } from '@/components/NodePackagingLevel/NodePackagingLevel';
import { useTranslation } from '@/hooks/useTranslation';
import { PersistedItem } from '@/types/DataModel';

import { useHierarchyElementsFilter } from '../../hooks/useHierarchyElementsFilter';
import { useItemHierarchyName } from '../../hooks/useItemHierarchyName';
import { useItemHierarchy,useItems } from '../../ItemEditor.queries';
import { useHierarchyTree } from './hooks/useNavigationDropdownMenu';
import { useNavigationDropdownValidationUpdater } from './hooks/useNavigationDropdownValidationUpdater';

// Icon mapping for different hierarchy types
const HIERARCHY_ICON_MAP: Record<string, React.ComponentType<any>> = {
    'PackagingUnitBase': PackagingUnitBase,
    'PackagingUnitCase': PackagingUnitCase,
    'PackagingUnitDisplay': PackagingUnitDisplay,
    'PackagingUnitMixedModule': PackagingUnitDisplay, // FIXME: Add a corresponding icon for gdsn type 'MIXED_MODULE'!
    'PackagingUnitPack': PackagingUnitCase, // FIXME: Add a corresponding icon for gdsn type 'PACK_OR_INNER_PACK'!
    'PackagingUnitPallet': PackagingUnitPallet,
    'PackagingUnitContainer': PackagingUnitContainer,
    'packaging': PackagingUnitDisplay
};

interface NavigationDropdownMenuProps {
    item: PersistedItem | undefined;
    isCreatingNewItem: boolean;
    onNavigateToItem?: (primaryKey: string) => void;
}

const NavigationDropdownMenu = forwardRef<HTMLDivElement, NavigationDropdownMenuProps>(({ item, isCreatingNewItem, onNavigateToItem }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [itemToNavigate, setItemToNavigate] = useState<string>('');
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [isNavigating, setIsNavigating] = useState(false);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const hasInitiallyLoaded = useRef(false);
    const previousHierarchyData = useRef<typeof hierarchiesData>(undefined);

    const { isDirty } = useFormState();
    const { reset } = useFormContext();
    const { t } = useTranslation();
    const { itemHierarchyName, isLoading: isItemHierarchyNameLoading } = useItemHierarchyName(item || {} as PersistedItem);

    const { data: hierarchiesData, isLoading: isHierarchyDataLoading } = useItemHierarchy({
        variables: {
            primaryKey__: item?.primaryKey__ || '',
            hierarchyName: itemHierarchyName || ''
        },
        enabled: !isEmpty(item?.primaryKey__) && !isEmpty(itemHierarchyName) && !isItemHierarchyNameLoading
    });

    const hierarchyNodes = useHierarchyTree(hierarchiesData);

    // Get all items from the hierarchy
    const items = useMemo(
        () => (isArray(hierarchiesData) ? hierarchiesData.map(element => element?.primaryKey) : []),
        [hierarchiesData]
    );

    // Subscribe to validation finished events so hasWarnings/isCompliant stay in sync
    useNavigationDropdownValidationUpdater({ hierarchyItemIds: items });

    // Fetch all items data from the hierarchy using findByPrimaryKey endpoint
    const { data: allItemsData, isLoading: isAllItemsDataLoading } = useItems({
        variables: { primaryKeys: items },
        enabled: !isEmpty(items)
    });

    const itemFallback = useMemo(
        () => [{ ...item, itemPrimaryKey: item?.primaryKey__ }],
        [item]
    );

    // Use the custom hook for hierarchy elements filter logic
    const { enhancedHierarchyNodes } = useHierarchyElementsFilter({
        hierarchyNodes: !isEmpty(hierarchyNodes) ? hierarchyNodes : itemFallback,
        allItemsData: allItemsData || itemFallback,
        hierarchiesData: hierarchiesData || itemFallback,
        isAllItemsDataLoading,
        hierarchyIconMap: HIERARCHY_ICON_MAP
    });

    // Filter hierarchy nodes based on search query
    const filteredHierarchyNodes = useMemo(() => {
        if (!searchQuery.trim() || !enhancedHierarchyNodes) {
            return enhancedHierarchyNodes;
        }

        const query = searchQuery.toLowerCase().trim();
        return enhancedHierarchyNodes.filter((node: any) => {
            const titleOne = node.titleOne?.toLowerCase() || '';
            const titleTwo = node.titleTwo?.toLowerCase() || '';
            const quantity = node.showQuantity && node.quantity != null
                ? String(node.quantity)
                : '';

            return titleOne.includes(query) || titleTwo.includes(query) || quantity.includes(query);
        });
    }, [enhancedHierarchyNodes, searchQuery]);

    // Check if we should show search (more than 20 levels)
    const shouldShowSearch = enhancedHierarchyNodes && enhancedHierarchyNodes.length > 20;

    // Automatically open dropdown when hierarchy data updates (but not on initial load or navigation)
    useEffect(() => {
        if (hierarchiesData && hierarchiesData.length > 0 && !isCreatingNewItem) {
            if (hasInitiallyLoaded.current && previousHierarchyData.current) {
                // Check if this is an actual data update (not just navigation) React Query
                // structural sharing gives stable references when data is deeply equal
                if (hierarchiesData !== previousHierarchyData.current) {
                    setIsOpen(true);
                }
            } else {
                hasInitiallyLoaded.current = true;
            }
            previousHierarchyData.current = hierarchiesData;
        }
    }, [hierarchiesData, isCreatingNewItem]);

    // Reset navigating state when item changes
    useEffect(() => {
        setIsNavigating(false);
    }, [item?.primaryKey__]);

    const handleDropdownOpenChange = (open: boolean) => {
        setIsOpen(open);
        if (!open) {
            // Clear search when dropdown closes
            setSearchQuery('');
        }
    };

    const handleItemClick = (primaryKey: string) => {
        if (!onNavigateToItem || primaryKey === item?.primaryKey__) return;

        if (!isDirty) {
            setIsOpen(false);
            setIsNavigating(true);
            onNavigateToItem(primaryKey);
        } else {
            setItemToNavigate(primaryKey);
            setShowConfirmationModal(true);
        }
    };

    useEffect(() => {
        if (!isOpen) return;

        // Wait for the dropdown element to appear in the DOM
        const waitForElement = () => {
            const dropdownEl = document.getElementById('navigation-dropdown-menu');
            if (dropdownEl) {
                // Find the selected NodePackagingLevel in the dropdown DOM
                const selectedNode = dropdownEl.querySelector('[data-node-selected="true"]');
                if (selectedNode) {
                    selectedNode.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
                return true;
            }
            return false;
        };

        // Check immediately in case element is already in DOM
        if (waitForElement()) return;

        // Use MutationObserver to watch for when the element is added
        const observer = new MutationObserver(() => {
            if (waitForElement()) {
                observer.disconnect();
            }
        });

        // Start observing the document body for changes
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        return () => {
            observer.disconnect();
        };
    }, [isOpen, item?.primaryKey__]);

    const hasInvalidHierarchyNodes = enhancedHierarchyNodes !== undefined && !enhancedHierarchyNodes;

    if (isItemHierarchyNameLoading || isHierarchyDataLoading || isAllItemsDataLoading || isNavigating || hasInvalidHierarchyNodes ) {
        return <Loader/>;
    }
    const selectedItemHierarchyElementTitleFilter = enhancedHierarchyNodes?.find((el: any) => el?.primaryKey === item?.primaryKey__);
    const isHierarchyElementTitlesEmpty = isEmpty(selectedItemHierarchyElementTitleFilter?.titleOne) && isEmpty(selectedItemHierarchyElementTitleFilter?.titleTwo);

    if ((!isCreatingNewItem &&((enhancedHierarchyNodes && ( !isEmpty(enhancedHierarchyNodes) && enhancedHierarchyNodes?.length < 2)) || isEmpty(enhancedHierarchyNodes)))) {
        if (isHierarchyElementTitlesEmpty) {
            return null;
        }

        return (
            <LabelPanel
                titleOne={selectedItemHierarchyElementTitleFilter?.titleOne}
                titleTwo={selectedItemHierarchyElementTitleFilter?.titleTwo}
                icon={selectedItemHierarchyElementTitleFilter?.icon || HIERARCHY_ICON_MAP['packaging']}
                titleOneHasMaxWidth={true}
                badgeTitle={selectedItemHierarchyElementTitleFilter.badge}
            />
        );
    }

    return (
            <>
                {(!isCreatingNewItem && enhancedHierarchyNodes && enhancedHierarchyNodes?.length > 1 )&& <DropdownMenu open={isOpen} onOpenChange={handleDropdownOpenChange} modal={false}>
                    <DropdownMenuTrigger asChild>
                        {!isHierarchyElementTitlesEmpty && <ButtonDropdown
                            titleOne={selectedItemHierarchyElementTitleFilter?.titleOne}
                            titleTwo={selectedItemHierarchyElementTitleFilter?.titleTwo}
                            icon={selectedItemHierarchyElementTitleFilter?.icon}
                            isOpen={isOpen}
                            titleOneHasMaxWidth= {true}
                        />}
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        hasBorder={true}
                        hasPadding={false}
                        id='navigation-dropdown-menu'
                        className={`tw-overflow-y-auto tw-overflow-x-hidden
                            ${shouldShowSearch
                ? 'tw-max-h-[488px] sm:tw-max-h-[488px] md:tw-max-h-[488px] tw-h-[480px] sm:tw-h-[480px] md:tw-h-[530px] tw-w-[480px] lg:tw-w-[536px]'
                : 'tw-max-h-[493px] sm:tw-max-h-[493px] md:tw-max-h-[493px]'
            }`}
                    >
                        {shouldShowSearch && (
                            <div className='tw-py-5 tw-px-3 tw-border-solid !tw-border !tw-border-primary-active
                            !tw-border-l-0 !tw-border-r-0 !tw-border-t-0 tw-sticky tw-top-0 tw-z-10 tw-bg-white'>
                                <Input
                                    placeholder={t('SEARCH')}
                                    suffixIcon={MagnifyingGlassIcon}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        )}
                        {filteredHierarchyNodes?.map((node: any, index: number) => {

                            const icon = node.icon || HIERARCHY_ICON_MAP['packaging'];
                            const hasWarnings = !isEmpty(node.itemData?.validation_warnings__);
                            const isCompliant = isEmpty(node.itemData?.validation__);
                            const isSelected = node.primaryKey === item?.primaryKey__;
                            const isFirstItem = index === 0;
                            const isLastItem = index === filteredHierarchyNodes.length - 1;
                            const nextLowerQuantity = node.showQuantity ? node.quantity : undefined;

                            return (
                                <div
                                    className={`tw-max-w-full ${isFirstItem && shouldShowSearch ?
                                        'tw-sticky tw-top-[77px] tw-z-10' : isFirstItem ? 'tw-sticky tw-top-0 tw-z-10' : ''}`}
                                    key={node.primaryKey}
                                    data-node-selected={isSelected ? 'true' : undefined}
                                >
                                    <NodePackagingLevel
                                        title={node.titleOne}
                                        titleTwo={node.titleTwo}
                                        isDisabled={!isFirstItem && node.disabled}
                                        badge={node.badge}
                                        level={isEmpty(searchQuery)? node.level + 1: 1}
                                        hasWarnings={hasWarnings}
                                        isCompliant={isCompliant}
                                        icon={icon}
                                        nextLowerQuantity={nextLowerQuantity}
                                        isLastItem={isLastItem}
                                        isFirstItem={isFirstItem}
                                        isSelected={isSelected}
                                        onClick={() => handleItemClick(node.primaryKey)}
                                        withoutPath= {!isEmpty(searchQuery)}
                                    />
                                </div>
                            );
                        })}
                    </DropdownMenuContent>
                </DropdownMenu>}

                <DiscardChangesConfirmationModal
                    show={showConfirmationModal}
                    onDiscard={() => {
                        if (onNavigateToItem && itemToNavigate !== item?.primaryKey__) {
                            setIsNavigating(true);
                            onNavigateToItem(itemToNavigate);
                            reset({},{ keepDirty: false });
                        }
                    }}
                    onClose={() => setShowConfirmationModal(false)}
                />
            </>
        );
});

NavigationDropdownMenu.displayName = 'NavigationDropdownMenu';

export { NavigationDropdownMenu };
